import React, { useEffect, useState } from 'react';
import Modal from 'components/Modal';
import { Button, Card, Col, Form, Input, Row, Space } from 'antd';
import { useMutation } from 'hooks/apollo';
import useCustomer from 'hooks/useCustomer';
import useFormatMessage from 'hooks/useFormatMessage';
import { gql } from '@apollo/client';
import globalMessages from 'translations/messages';
import SurveyCategorySelect from 'components/SurveyCategorySelect';

import targetImage from 'assets/images/target.png';
import feedbackImage from 'assets/images/feedback.png';
import generalImage from 'assets/images/general.svg';
import useGlobalContext from 'hooks/useGlobalContext';
import FormLabel from 'components/FormLabel';
import SurveyTemplateSelect from 'components/SurveyTemplateSelect';
import { SurveyType } from 'shared/services/prisma/types';
import LanguageSelect from 'components/LanguageSelect';

const MUTATION = gql`
  mutation CreateSurvey($input: CreateSurveyInput!) {
    createSurvey(input: $input) {
      id
      languageParent {
        id
      }
    }
  }
`;

const SelectableCard = ({ cardValue, children, value, onChange, coverImage, ...rest }) => (
  <Card className={value === cardValue ? 'selected' : ''} style={{ height: '100%' }} hoverable onClick={() => onChange(cardValue)} cover={<img src={coverImage} alt="" style={{ height: 100, width: 'auto', margin: '1em auto 0 auto' }} />} {...rest}>
    {children}
  </Card>
);

const SurveyTypeSelect = ({ value, onChange, possibleTypes }) => (
  <Row gutter={16}>
    {possibleTypes.includes(SurveyType.General) && (
    <Col span={8}>
      <SelectableCard cardValue={SurveyType.General} value={value} onChange={onChange} title="General survey" coverImage={generalImage}>
        Monitor overall satisfaction with a general survey. Response rate 2-10%.
      </SelectableCard>
    </Col>
    )}
    {possibleTypes.includes(SurveyType.Feedback) && (
    <Col span={8}>
      <SelectableCard cardValue={SurveyType.Feedback} value={value} onChange={onChange} title="Feedback" coverImage={feedbackImage}>
        Solve urgent issues with a feedback button.
      </SelectableCard>
    </Col>
    )}
    {possibleTypes.includes(SurveyType.Targeted) && (
    <Col span={8}>
      <SelectableCard cardValue={SurveyType.Targeted} value={value} onChange={onChange} title="Targeted survey" coverImage={targetImage}>
        Investigate a specific audience with a targeted survey. Response rate 10-40%.
      </SelectableCard>
    </Col>
    )}
  </Row>
);

const CreateSurvey = ({ open, onCancel, onDone, dataSource, type, possibleTypes }) => {
  const $t = useFormatMessage();
  const { message } = useGlobalContext();
  const [step, setStep] = useState();
  const { customer } = useCustomer();
  const [form] = Form.useForm();
  const [createSurvey, { loading: mutationLoading }] = useMutation(MUTATION);
  const [selectedType, setSelectedType] = useState();

  const selectedLanguage = Form.useWatch('language', form);

  const onFinish = async (values) => {
    if (step === 1) {
      setSelectedType(values.type);
      setStep(2);
    } else {
      const { surveyCategoryId, ...input } = values;
      try {
        const { data } = await createSurvey({
          variables: {
            input: {
              ...input,
              type: selectedType,
              categoryIds: surveyCategoryId ? [surveyCategoryId] : undefined,
              customerId: customer?.id,
              dataSourceId: dataSource?.id,
              websiteId: dataSource?.website?.id,
            },
          },
        });
        onDone(data?.createSurvey);
      } catch (e) {
        message.error($t(globalMessages.errorFormFeedback));
      }
    }
  };

  const initialValues = {
    language: dataSource?.website?.language || 'en-GB',
    surveyCategoryId: dataSource?.website?.categories?.find(x => x)?.id,
    type,
    templateId: '',
  };

  useEffect(() => {
    setStep(possibleTypes?.length === 1 ? 2 : 1);
    setSelectedType(type);
    form.setFieldsValue(initialValues);
  }, [open, possibleTypes, type, dataSource]);

  useEffect(() => {
    if (selectedType) {
      form.setFieldsValue({ title: $t(globalMessages[selectedType.toLowerCase()]) });
    }
  }, [selectedType]);

  useEffect(() => {
    form.setFieldsValue({ templateId: '' });
  }, [selectedLanguage]);

  let footer;
  let content;
  let width;

  if (step === 1) {
    footer = (
      <Space>
        <Button onClick={onCancel} className="create-survey-cancel">
          Cancel
        </Button>
        <Button type="primary" onClick={form.submit} className="create-survey-next">
          Next
        </Button>
      </Space>
    );
    content = (
      <Form.Item name="type" rules={[{ required: true, message: 'Select a type' }]}>
        <SurveyTypeSelect possibleTypes={possibleTypes} />
      </Form.Item>
    );
    width = 800;
  } else {
    footer = (
      <Space>
        <Button onClick={() => setStep(1)} className="create-survey-back">
          Back
        </Button>
        <Button type="primary" onClick={form.submit} loading={mutationLoading} className="create-survey-save">
          Create
        </Button>
      </Space>
    );

    content = (
      <>
        <Form.Item label={<FormLabel label="Title" description="Internal name of your survey, not visible to respondents." />} name="title" rules={[{ required: true, message: 'Title is required' }]}>
          <Input />
        </Form.Item>
        <Form.Item label={<FormLabel label="Primary language" description="You can add more languages later." />} name="language" rules={[{ required: true }]}>
          <LanguageSelect />
        </Form.Item>
        {selectedType === SurveyType.General ? (
          <Form.Item label={<FormLabel label="Industry" description="This will let you compare your result to other companies in the same industry." />} name="surveyCategoryId">
            <SurveyCategorySelect allowClear />
          </Form.Item>
        ) : (
          <Form.Item label={<FormLabel label="Template" description="Start from a preconfigured template." />} name="templateId">
            <SurveyTemplateSelect type={selectedType} language={selectedLanguage} />
          </Form.Item>
        )}
      </>
    );
  }

  return (
    <Modal
      title="Create survey"
      centered
      open={open}
      footer={footer}
      onCancel={onCancel}
      width={width}
    >
      <Form requiredMark={false} form={form} layout="vertical" initialValues={initialValues} onFinish={onFinish}>{content}</Form>
    </Modal>
  );
};

export default CreateSurvey;
